import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Property, AddPropertyAvailabilityRequest } from '../../../../../features/property/models/property.model'; // adjust path
import { AvailabilityService } from '../../../../../features/property/services/property-availability.service';
import { AuthService } from '../../../../../core/services/auth.service';
import { SnackBarService } from '../../../../services/snackbar.service';
import { tap } from 'rxjs';
import { HapticsService } from '../../../../general/services/haptics.service';

@Component({
  selector: 'app-add-property-booking-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDatepickerModule,
    MatInputModule,
    MatNativeDateModule,
    MatDialogModule
  ],
  templateUrl: './add-property-booking-form.html',
  styleUrl: './add-property-booking-form.scss'
})
export class AddPropertyBookingForm implements OnInit {
  private fb = inject(FormBuilder);
  private availabilityService = inject(AvailabilityService);
  private auth = inject(AuthService);
  private snackbar = inject(SnackBarService);
  private haptics = inject(HapticsService);
  readonly dialogRef = inject(MatDialogRef<AddPropertyBookingForm>);
  readonly data = inject<Property>(MAT_DIALOG_DATA);

  form!: FormGroup;
  property?: Property;
  username: string | null = null;

  reasons: string[] = ['Booked', 'Owner', 'Maintenance'];

  ngOnInit() {
    this.property = this.data;

    this.form = this.fb.group({
      startDate: [null, Validators.required],
      endDate: [null, Validators.required],
      reason: [null, Validators.required],
      reasonText: [null]
    }, { validators: this.dateRangeValidator });

    this.auth.loggedInUserId$.pipe(
      tap(userId => this.username = userId)
    ).subscribe();
  }

  dateRangeValidator(group: AbstractControl): ValidationErrors | null {
    const start = group.get('startDate')?.value;
    const end = group.get('endDate')?.value;

    if (start && end && new Date(end) < new Date(start)) {
      group.get('endDate')?.setErrors({ invalidRange: true });
      return { invalidRange: true };
    }
    return null;
  }

  get startDateCtrl() { return this.form.get('startDate'); }
  get endDateCtrl() { return this.form.get('endDate'); }
  get reasonCtrl() { return this.form.get('reason'); }
  get reasonTextCtrl() { return this.form.get('reasonText'); }


  onCancel(): void {
    this.haptics.click();
    this.dialogRef.close();
  }

  onSave(): void {
    this.haptics.click();
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.snackbar.open('Please fill all required fields correctly', 'Close', { duration: 4000 });
      return;
    }

    const { startDate, endDate, reason, reasonText } = this.form.value;

    const current = new Date(startDate);
    const end = new Date(endDate);

    // TODO: prevent overlapping or already booked dates – you'd need to check via service first

    while (current <= end) {
      const dateStr = current.toISOString().split('T')[0];

      if (this.property) {
        const request: AddPropertyAvailabilityRequest = {
          propertyId: this.property.id,
          date: dateStr,
          sourceId: 1,
          currency: 'USD',
          sourceMetadata: JSON.stringify({ note: 'manual', user: this.username }),
          sourceUpdatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          status: reason,
          blockedReason: reasonText,
          priceCents: null,
          minNights: null,
          syncStatus: 'synced',
          syncedAt: new Date().toISOString()
        };

        this.createAvailability(request);
      }

      current.setDate(current.getDate() + 1);
    }

    // Close dialog after initiating all requests
    // (You might want to wait for all to complete with forkJoin / combineLatest)
    this.dialogRef.close(true); // or pass back success flag
  }

  private createAvailability(request: AddPropertyAvailabilityRequest) {
    this.availabilityService.createPropertyAvailability(request).subscribe({
      next: () => {
        this.snackbar.open(`Dates scheduled successfully`, 'OK', {
          duration: 4000,
          panelClass: ['success-snackbar']
        });
      },
      error: (err) => {
        console.error('Failed to book date', request.date, err);
        this.snackbar.open(`Failed to book ${request.date}. Try again.`, 'Close', {
          duration: 6000,
          panelClass: ['error-snackbar']
        });
      }
    });
  }
}